/**
 * One month, day by day, against where the model says it should be by now.
 *
 * A month in progress is judged against PLAN TO DATE — the part of the month's
 * plan that falls on the days already gone, on the daily ramp — not against
 * the whole month. The projection carries today's pace to month end on the
 * same ramp, and is the number the Monthly view's paced month shows.
 *
 * Beside the plan sits the RAMPED plan — the model at our own spend level —
 * and a card for tomorrow, so the view answers "what should we spend next".
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { Loader2 } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui'
import ModelChart from './ModelChart'
import { Tile, Notice } from './parts'
import { scienceModelAPI } from '../../services/api'
import { monthToDate, addDays, DAILY_METRICS } from '../../lib/scienceModelDaily'
import { FORMATTERS, formatVariance, monthLabel, variance } from '../../lib/scienceModelView'

/** Today's date in Eastern time, the reporting timezone. */
function todayEt() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(new Date())
}

/** "Fri Sep 18" */
function dayLabel(date) {
  return new Date(`${date}T12:00:00Z`).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC',
  })
}

/** A plan day by date, from any month of a dailyPlan. */
function findDay(daily, date) {
  const month = daily?.find(entry => entry.month === date.slice(0, 7))
  return month?.days.find(day => day.date === date) || null
}

const CUMULATIVE_NAMES = {
  plan: 'Plan to date',
  actual: 'Actual to date',
  actualPartial: 'Actual so far today',
  reforecast: 'Projection',
  reforecastPartial: 'Projection',
}

const DAILY_NAMES = { plan: 'Plan', actual: 'Actual', actualPartial: 'Actual so far', ramped: 'Ramped plan' }

const TABLE_METRICS = ['newCustomers', 'revenue', 'spend']

export default function DailyView({ plan, daily, ramped }) {
  const months = useMemo(() => plan.map(row => row.month), [plan])
  const [month, setMonth] = useState(() => {
    const current = todayEt().slice(0, 7)
    return months.includes(current) ? current : months[0]
  })
  // Keep the chosen month inside the horizon when the start month moves.
  useEffect(() => {
    if (!months.includes(month)) setMonth(months[0])
  }, [months, month])

  const [actual, setActual] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const fetchDaily = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await scienceModelAPI.getDailyActuals({ month })
      if (response.data.success) setActual(response.data.data)
    } catch (err) {
      console.error('[SCIENCE-MODEL] Daily error:', err)
      setError(err.response?.data?.error?.message || err.message)
      setActual(null)
    } finally {
      setLoading(false)
    }
  }, [month])

  useEffect(() => { fetchDaily() }, [fetchDaily])

  const monthPlan = daily.find(entry => entry.month === month)
  const rampedMonth = ramped?.daily.find(entry => entry.month === month) || null
  // A response for a month other than the one selected is stale.
  const usable = actual?.month === month ? actual : null

  const mtd = useMemo(() => (monthPlan
    ? Object.fromEntries(Object.keys(DAILY_METRICS).map(metric => [metric, monthToDate(monthPlan, usable, metric)]))
    : null
  ), [monthPlan, usable])
  // The same arithmetic on the ramped plan. Only its plan figures are read:
  // the actuals it is held against are the same ones.
  const rampedMtd = useMemo(() => (rampedMonth
    ? Object.fromEntries(Object.keys(DAILY_METRICS).map(metric => [metric, monthToDate(rampedMonth, usable, metric)]))
    : null
  ), [rampedMonth, usable])

  // Only for the render between the start month moving and the effect above
  // pulling the selection back inside the horizon.
  if (!monthPlan || !mtd) return null

  const started = usable && usable.days_elapsed > 0
  const dayOf = started ? Math.ceil(usable.days_elapsed) : 0
  const note = !usable
    ? (loading ? 'Loading…' : 'No actuals')
    : !started
      ? `${monthLabel(month)} has not started`
      : mtd.newCustomers.complete
        ? `${monthLabel(month)}, complete`
        : `Through day ${dayOf} of ${monthPlan.days.length}, counting today so far`

  const cacToDate = started && mtd.newCustomers.actualToDate > 0 ? mtd.spend.actualToDate / mtd.newCustomers.actualToDate : null
  const planCac = mtd.newCustomers.planTotal > 0 ? mtd.spend.planTotal / mtd.newCustomers.planTotal : null

  const projectionNote = (metric) => {
    const m = mtd[metric]
    if (!started || m.complete || m.projection == null) return note
    const full = FORMATTERS[DAILY_METRICS[metric].format].full
    return `Month-end projection ${full(m.projection)} vs plan ${full(m.planTotal)} (${formatVariance(variance(m.projection, m.planTotal))})`
  }

  const cumulative = (metric) => mtd[metric].points.map((p, i) => ({
    date: p.date, label: p.label, plan: p.cumPlan, actual: p.cumActual, reforecast: p.projection, partial: p.partial,
    ramped: rampedMtd ? rampedMtd[metric].points[i].cumPlan : null,
  }))
  const perDay = (metric) => mtd[metric].points.map((p, i) => ({
    date: p.date, label: p.label, plan: p.plan, actual: p.actual, partial: p.partial,
    ramped: rampedMtd ? rampedMtd[metric].points[i].plan : null,
  }))

  const rampedTile = (metric) => (rampedMtd ? {
    ramped: rampedMtd[metric].planToDate,
    rampedLabel: 'Ramped to date',
    rampedDelta: started ? variance(mtd[metric].actualToDate, rampedMtd[metric].planToDate) : null,
  } : {})

  // Tomorrow, whichever month it falls in — the answer to "what do we spend next".
  const asOf = usable?.asOf || todayEt()
  const tomorrow = addDays(asOf, 1)
  const tomorrowRamped = findDay(ramped?.daily, tomorrow)
  const tomorrowPlan = findDay(daily, tomorrow)
  const currentMonthSelected = asOf.slice(0, 7) === month
  const spendGap = rampedMtd && started && !mtd.spend.complete
    ? rampedMtd.spend.planToDate - mtd.spend.actualToDate
    : null
  const rampedLeft = rampedMtd && currentMonthSelected ? rampedMtd.spend.planTotal - rampedMtd.spend.planToDate : null

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <label className="space-y-1">
          <span className="block text-xs text-muted-foreground">Month</span>
          <select
            value={month}
            onChange={e => setMonth(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground"
          >
            {months.map(m => <option key={m} value={m}>{monthLabel(m)}</option>)}
          </select>
        </label>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground mb-2.5" />}
      </div>

      {error && <Notice tone="error">Daily actuals could not be loaded: {error}. The daily plan is shown on its own.</Notice>}
      <Notice>
        Each month&apos;s plan ramps across its days toward the next month&apos;s pace, and renewals land on the day
        their cohort bought, so plan to date is where the model says this month should be by now — not a straight
        share of the month. Today counts for the part of it that has passed.
      </Notice>
      {usable?.ad_spend_available === false && (
        <Notice>No ad account has reported spend for Science.co, so actual spend and CAC read as zero.</Notice>
      )}

      {tomorrowRamped && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Tomorrow · {dayLabel(tomorrow)}</CardTitle>
            <CardDescription className="text-xs">
              The ramped plan for tomorrow: the model at our spend level, on its daily ramp. The sheet&apos;s plan is
              shown for scale.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Ad spend', metric: 'spend', format: 'money' },
              { label: 'New customers', metric: 'newCustomers', format: 'count' },
              { label: 'Revenue', metric: 'revenue', format: 'money' },
            ].map(({ label, metric, format }) => (
              <div key={metric} className="space-y-0.5">
                <div className="text-xs text-muted-foreground">{label}</div>
                <div className="text-xl font-semibold tabular-nums">{FORMATTERS[format].full(tomorrowRamped[metric])}</div>
                <div className="text-xs text-muted-foreground tabular-nums">Sheet plan {FORMATTERS[format].full(tomorrowPlan?.[metric])}</div>
              </div>
            ))}
            <div className="space-y-0.5">
              <div className="text-xs text-muted-foreground">Spend vs ramped plan so far</div>
              <div className="text-xl font-semibold tabular-nums">
                {spendGap == null ? '—' : spendGap > 0 ? `${FORMATTERS.money.full(spendGap)} under` : `${FORMATTERS.money.full(-spendGap)} over`}
              </div>
              <div className="text-xs text-muted-foreground tabular-nums">
                {rampedLeft != null ? `${FORMATTERS.money.full(rampedLeft)} left in ${monthLabel(month)} at ramped plan` : 'This month to date'}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <Tile
          title="New customers to date" format="count" planLabel="Plan to date"
          value={started ? mtd.newCustomers.actualToDate : null} plan={mtd.newCustomers.planToDate}
          delta={started ? variance(mtd.newCustomers.actualToDate, mtd.newCustomers.planToDate) : null}
          note={projectionNote('newCustomers')}
          {...rampedTile('newCustomers')}
        />
        <Tile
          title="Revenue to date" planLabel="Plan to date"
          value={started ? mtd.revenue.actualToDate : null} plan={mtd.revenue.planToDate}
          delta={started ? variance(mtd.revenue.actualToDate, mtd.revenue.planToDate) : null}
          note={projectionNote('revenue')}
          {...rampedTile('revenue')}
        />
        <Tile
          title="Ad spend to date" planLabel="Plan to date"
          value={started ? mtd.spend.actualToDate : null} plan={mtd.spend.planToDate}
          delta={started ? variance(mtd.spend.actualToDate, mtd.spend.planToDate) : null}
          note={projectionNote('spend')}
          {...rampedTile('spend')}
        />
        <Tile
          title="CAC to date" invert
          value={cacToDate} plan={planCac}
          delta={cacToDate != null ? variance(cacToDate, planCac) : null}
          note={note}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <ModelChart title="New customers, cumulative" format="count" height={240} data={cumulative('newCustomers')} names={CUMULATIVE_NAMES} labelPrefix={`${monthLabel(month)} · day `} />
        <ModelChart title="Revenue, cumulative" height={240} data={cumulative('revenue')} names={CUMULATIVE_NAMES} labelPrefix={`${monthLabel(month)} · day `} />
        <ModelChart title="Ad spend, cumulative" height={240} data={cumulative('spend')} names={CUMULATIVE_NAMES} labelPrefix={`${monthLabel(month)} · day `} />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <ModelChart title="New customers per day" format="count" height={220} data={perDay('newCustomers')} names={DAILY_NAMES} showReforecast={false} labelPrefix={`${monthLabel(month)} · day `} />
        <ModelChart title="Revenue per day" description="New plus renewals" height={220} data={perDay('revenue')} names={DAILY_NAMES} showReforecast={false} labelPrefix={`${monthLabel(month)} · day `} />
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-xs tabular-nums">
            <thead>
              <tr className="border-b">
                <th rowSpan={2} className="sticky left-0 bg-card px-3 py-2 text-left font-medium text-muted-foreground">Day</th>
                {TABLE_METRICS.map(metric => (
                  <th key={metric} colSpan={rampedMtd ? 5 : 4} className="px-2 pt-2 text-center font-semibold border-l">{DAILY_METRICS[metric].label}</th>
                ))}
              </tr>
              <tr className="border-b">
                {TABLE_METRICS.map(metric => (rampedMtd ? ['Plan', 'Ramped', 'Actual', 'To date', 'vs ramped to date'] : ['Plan', 'Actual', 'To date', 'vs plan to date']).map((h, i) => (
                  <th key={`${metric}-${h}`} className={`px-2 pb-2 text-right font-medium text-muted-foreground whitespace-nowrap ${i === 0 ? 'border-l' : ''}`}>{h}</th>
                )))}
              </tr>
            </thead>
            <tbody>
              {monthPlan.days.map((planDay, i) => (
                <tr key={planDay.date} className="border-b last:border-0">
                  <td className="sticky left-0 bg-card px-3 py-1.5 whitespace-nowrap">
                    {monthLabel(month).split(' ')[0]} {planDay.day}
                    {mtd.newCustomers.points[i].partial && <span className="ml-1 text-muted-foreground">(so far)</span>}
                  </td>
                  {TABLE_METRICS.map(metric => {
                    const full = FORMATTERS[DAILY_METRICS[metric].format].full
                    const point = mtd[metric].points[i]
                    const rampedPoint = rampedMtd ? rampedMtd[metric].points[i] : null
                    // Against the ramped plan when there is one: it is the plan
                    // at the spend we actually run, so its gap is the actionable one.
                    const base = rampedPoint ? rampedPoint.cumPlan : point.cumPlan
                    return [
                      <td key="p" className="px-2 py-1.5 text-right border-l">{full(point.plan)}</td>,
                      rampedPoint && <td key="rp" className="px-2 py-1.5 text-right">{full(rampedPoint.plan)}</td>,
                      <td key="a" className="px-2 py-1.5 text-right">{point.actual != null ? full(point.actual) : ''}</td>,
                      <td key="c" className="px-2 py-1.5 text-right">{point.cumActual != null ? full(point.cumActual) : ''}</td>,
                      <td key="v" className="px-2 py-1.5 text-right">
                        {point.cumActual != null ? formatVariance(variance(point.cumActual, base)) : ''}
                      </td>,
                    ]
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  )
}
